import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, useApp } from "../context/AppContext";
import ImageOrArt from "./ImageOrArt";
import GeoArt from "./GeoArt";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartTotal } = useApp();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: cart.map((p) => ({ lookup_key: p.lookup_key, quantity: p.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[80] w-full max-w-md bg-card border-l border-border flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="font-display font-extrabold uppercase text-lg">Your Cart</h2>
              <button onClick={() => setCartOpen(false)} aria-label="Close cart" data-testid="cart-close" className="w-10 h-10 flex items-center justify-center border border-border hover:border-[#FF1A1A] hover:text-[#FF1A1A] transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {cart.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="cart-empty">Your cart is empty. Go collect something.</p>
              )}
              {cart.map((p) => (
                <div key={p.id} className="flex gap-4" data-testid={`cart-item-${p.id}`}>
                  <div className="w-20 shrink-0 border border-border overflow-hidden">
                    <ImageOrArt image={p.image} seed={p.seed} ratio="4/5" className="w-full h-full" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display font-bold uppercase text-sm leading-tight">{p.name}</p>
                    <p className="font-mono-x text-xs text-muted-foreground mt-1">${p.price.toLocaleString()}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <button onClick={() => updateQty(p.id, -1)} aria-label="Decrease quantity" data-testid={`cart-minus-${p.id}`} className="w-7 h-7 border border-border flex items-center justify-center hover:border-[#FF1A1A]"><Minus size={12} /></button>
                      <span className="font-mono-x text-sm" data-testid={`cart-qty-${p.id}`}>{p.qty}</span>
                      <button onClick={() => updateQty(p.id, 1)} aria-label="Increase quantity" data-testid={`cart-plus-${p.id}`} className="w-7 h-7 border border-border flex items-center justify-center hover:border-[#FF1A1A]"><Plus size={12} /></button>
                      <button onClick={() => removeFromCart(p.id)} aria-label="Remove item" data-testid={`cart-remove-${p.id}`} className="ml-auto text-muted-foreground hover:text-[#FF1A1A]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {cart.length > 0 && (
              <div className="p-6 border-t border-border space-y-4">
                <div className="flex justify-between font-display font-bold uppercase">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">${cartTotal.toLocaleString()}</span>
                </div>
                <p className="text-xs text-muted-foreground">Taxes calculated at checkout. Test mode — no real charges.</p>
                <button onClick={checkout} disabled={loading} className="btn-red w-full" data-testid="stripe-checkout-button">
                  {loading ? "Redirecting…" : "Checkout with Stripe"}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
